import supabase from '@/supabase'
import type { CarDraft, CompositeCarDraft, CreateCarDraft, UpdateCarDraft } from '@/types/supabase'
import { UNTITLED_CAR_DRAFT } from '@/utils'

class CarDraftsApi {

  public async fetchCarDrafts(page: number, limit: number | null, sortBy: SortBy<CarDraft>): Promise<CompositeCarDraft[]> {
    let query = supabase.from('car_drafts')
      .select('*, created_by_user:users!car_drafts_created_by_fkey(first_name, last_name)')
    for (const { key, ascending } of sortBy.keys) {
      query = query.order(key, { ascending })
    }
    if (limit !== null) {
      query.range((page - 1) * limit, page * limit - 1)
    }
    const { data, error } = await query
    if (error) throw error
    return data.map(draft => ({ ...draft, title: draft.title ?? UNTITLED_CAR_DRAFT }))
  }

  public async fetchCarDraft(carDraftId: string): Promise<CarDraft> {
    const { data, error } = await supabase.from('car_drafts')
      .select('*')
      .eq('id', carDraftId)
      .single()
    if (error) throw error
    return { ...data, title: data.title ?? UNTITLED_CAR_DRAFT }
  }

  public async createOrUpdateCarDraft(carDraft: CreateCarDraft | UpdateCarDraft) : Promise<string> {
    if (carDraft.id) {
      const { error } = await supabase.from('car_drafts')
        .update(carDraft)
        .eq('id', carDraft.id)
      if (error) throw error
      return carDraft.id
    } else {
      const { data, error } = await supabase.from('car_drafts')
        .insert(carDraft)
        .select('id')
        .single()
      if (error) throw error
      return data.id
    }
  }

  public async deleteCarDraft(carDraftId: string): Promise<void> {
    const { error } = await supabase.from('car_drafts')
      .delete()
      .eq('id', carDraftId)
    if (error) throw error
  }

  public async fetchCarDraftTitleByIds(carDraftIds: string[]): Promise<Map<string, string>> {
    const { data, error } = await supabase.from('car_drafts')
      .select('id, title')
      .in('id', carDraftIds)
    if (error) throw error
    return new Map(data.map(d => [d.id, d.title ?? UNTITLED_CAR_DRAFT]))
  }

  public async fetchCarDraftsCount(): Promise<number> {
    const { count, error } = await supabase.from('car_drafts')
      .select('*', { count: 'exact', head: true })
    if (error) throw error
    return count ?? 0
  }
}

export default new CarDraftsApi()
